/**
 * CountrySelector.js
 * ===================
 * Dropdown to switch between US and India markets
 * Changes the entire dashboard data context
 */

import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

// Country configurations
const COUNTRIES = {
  US: {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    currencySymbol: '$',
    indices: ['S&P 500', 'Dow Jones', 'NASDAQ', 'Russell 2000'],
    exchanges: ['NYSE', 'NASDAQ'],
    defaultStock: 'AAPL',
    marketHours: '9:30 AM - 4:00 PM EST',
    timezone: 'America/New_York'
  },
  IN: {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    currency: 'INR',
    currencySymbol: '₹',
    indices: ['NIFTY 50', 'SENSEX', 'BANK NIFTY', 'NIFTY IT'],
    exchanges: ['NSE', 'BSE'],
    defaultStock: 'RELIANCE',
    marketHours: '9:15 AM - 3:30 PM IST',
    timezone: 'Asia/Kolkata'
  }
};

const CountrySelector = ({ selectedCountry, onCountryChange, style = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentCountry = COUNTRIES[selectedCountry] || COUNTRIES.US;

  return (
    <div 
      ref={dropdownRef}
      style={{ 
        position: 'relative', 
        display: 'inline-block',
        ...style 
      }}
    >
      {/* Selected Country Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          border: '1px solid #334155',
          borderRadius: '0.5rem',
          color: 'white',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: '500',
          transition: 'all 0.2s ease',
          minWidth: '120px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#3b82f6';
          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#334155';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <span style={{ fontSize: '1.25rem' }}>{currentCountry.flag}</span>
        <span>{currentCountry.code}</span>
        <ChevronDown 
          size={16} 
          style={{ 
            marginLeft: 'auto',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            minWidth: '200px',
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid #334155',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          {Object.values(COUNTRIES).map((country) => (
            <button
              key={country.code}
              onClick={() => {
                onCountryChange(country.code);
                setIsOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                width: '100%',
                padding: '0.75rem 1rem',
                background: selectedCountry === country.code 
                  ? 'rgba(59, 130, 246, 0.2)' 
                  : 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (selectedCountry !== country.code) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCountry !== country.code) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '1.5rem' }}>{country.flag}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                  {country.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  {country.exchanges.join(' / ')} • {country.currency}
                </div>
              </div>
              {selectedCountry === country.code && (
                <Check size={16} color="#22c55e" />
              )}
            </button>
          ))}

          {/* Market Info Footer */}
          <div style={{
            padding: '0.75rem 1rem',
            borderTop: '1px solid #334155',
            background: 'rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              fontSize: '0.75rem',
              color: '#94a3b8'
            }}>
              <Globe size={14} />
              <span>Market Hours: {currentCountry.marketHours}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Export countries config for use in other components
export { COUNTRIES };
export default CountrySelector;
